<template>
  <div>
    <!-- Show AltLocationDetail for West Texas S&B Pipeline -->
    <AltLocationDetail v-if="isPipelineLocation" />
    
    <!-- Default location detail view for all other locations -->
    <v-container v-else>
      <v-row>
        <v-col cols="12" md="12">
          <h1>{{ $store.state.location.name }}</h1>
          <p>{{ fullAddress }}</p>
          <v-spacer/>
          <v-btn v-if="!isDemo" dark color="primary" @click="goToNewDevice">Add Device</v-btn>
        </v-col>
        <v-col>
        </v-col>
      </v-row>
      <v-row>
        <!-- Simple Placeholder Widget -->
        

        <!-- Status Donut Chart -->
        <v-col cols="12" md="4">
          <v-card class="pa-4" outlined min-height="275">
            <v-card-title>Device Status</v-card-title>
            <v-card-text>
              <DoughnutChart 
                :status-filter="$store.state.statusFilter"
                :device-counts="deviceStatusCounts"
                @filter-status="updateFilter"
              />
            </v-card-text>
          </v-card>
        </v-col>

        <!-- Replace the Alert Count Widget with this OEE Widget -->
        <v-col cols="12" md="4">
          <v-card class="pa-4" outlined min-height="275">
            <v-card-title class="d-flex align-center">
              <span>Overall Efficiency</span>
              <v-chip
                small
                :color="oeeStatus.color"
                text-color="black"
                class="ml-2"
              >
                {{ oeeStatus.text }}
              </v-chip>
            </v-card-title>
            <v-card-text>
              <div class="metrics-container">
                <!-- Add headers -->
                <div class="metric-row header">
                  <span></span>
                  <div class="metric-values">
                    <span class="column-header">ACTUAL</span>
                    <span class="column-header">TARGET</span>
                  </div>
                </div>
                <!-- Update existing metric rows -->
                <div class="metric-row">
                  <span class="metric-label">Parts</span>
                  <div class="metric-values">
                    <span class="actual" :class="{ 'red--text': partsEfficiency < 90 }">{{ actualParts }}</span>
                    <span class="target">{{ targetParts }}</span>
                  </div>
                </div>
                <div class="metric-row">
                  <span class="metric-label">Cycle Time</span>
                  <div class="metric-values">
                    <span class="actual" :class="{ 'red--text': cycleEfficiency < 90 }">{{ actualCycle }}</span>
                    <span class="target">{{ targetCycle }}</span>
                  </div>
                </div>
                <div class="metric-row">
                  <span class="metric-label">Uptime</span>
                  <div class="metric-values">
                    <span class="actual" :class="{ 'red--text': uptimeEfficiency < 90 }">{{ actualUptime }}</span>
                    <span class="target">{{ targetUptime }}</span>
                  </div>
                </div>
              </div>
              <v-progress-linear
                :value="overallOEE"
                height="20"
                :color="oeeStatus.color"
                class="mt-4"
              >
                <template v-slot:default>
                  <strong>{{ Math.round(overallOEE) }}%</strong>
                </template>
              </v-progress-linear>
            </v-card-text>
          </v-card>
        </v-col>

        <!-- Progress Bar Widget -->
        <v-col cols="12" md="4">
          <v-card class="pa-4" outlined min-height="275">
            <v-card-title>Device Connectivity</v-card-title>
            <v-card-text>
              <div>
                {{ onlineSensors }} / {{ totalSensors }} Devices Online
              </div>
              <v-progress-linear
                :value="(onlineSensors / totalSensors) * 100"
                color="green"
                striped
                height="20"
                class="progress-bar"
              ></v-progress-linear>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Second Row: List of Sensors -->
      <v-row>
        <v-col cols="12">
          <v-card class="pa-4" outlined>
            <v-card-title>Sensors</v-card-title>
            <SensorTable 
              :sensors="sensors"
              :headers="sensorHeaders"
              :status-filter="$store.state.statusFilter"
              @filter-status="updateFilter"
              @view-details="goToSensor"
              @click:row="goToSensor"
            />
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </div>
</template>

<script>
import DoughnutChart from "./Components/DoughnutChart.vue";
import BatteryIndicator from './Components/BatteryIndicator.vue';
import TemperatureIndicator from './Components/TemperatureIndicator.vue';
import SensorTable from './Components/SensorTable.vue';
import AltLocationDetail from '../components/AltLocationDetail.vue';
import { fetchObjects } from '@/services/firebase-helpers';
import { fetchSingleObjectByKey } from "../services/firebase-helpers";

// Add this mixin before the export default
const animateValueMixin = {
  methods: {
    animateValue(start, end, duration = 1000) {
      return new Promise(resolve => {
        let currentValue = start;
        const startTime = performance.now();
        
        const animate = (currentTime) => {
          const elapsed = currentTime - startTime;
          const progress = Math.min(elapsed / duration, 1);

          currentValue = start + (end - start) * progress;
          
          if (progress < 1) {
            requestAnimationFrame(animate);
          } else {
            currentValue = end; // Ensure we end at exact target
            resolve(currentValue);
          }
          return Number(currentValue.toFixed(1));
        };

        requestAnimationFrame(animate);
      });
    }
  }
};

export default {
  components: {
    //Status
    DoughnutChart,
    BatteryIndicator,
    TemperatureIndicator,
    SensorTable,
    AltLocationDetail
  },
  mixins: [animateValueMixin],
  data() {
    return {
      item: null,
      id: null,
      alertCount: 2, // Example alert count
      sensorsWithAlerts: 2, // Sensors with alerts
      progressValue: 0,
      animatedValues: {
        parts: 0,
        cycle: 0,
        uptime: 0,
        offline: 0,
        total: 0,
        online: 0
      },
      sensorHeaders: [
        { text: 'Device ID', value: 'id', sortable: true },
        { text: 'Status', value: 'deviceStatus', sortable: true },
        { text: 'Last Seen', value: 'lastSeen', sortable: true },
        { text: 'Connectivity', value: 'connectivity', sortable: true },
        { text: 'Torque Deviation', value: 'rotationAngle', sortable: true },
        { text: 'Battery', value: 'batteryLevel', sortable: true },
        { text: 'Temperature (°C)', value: 'temperature', sortable: true },
        { text: 'Actions', value: 'actions', sortable: false }
      ],
    };
  },
  watch: {
    "$route.params.id": "fetchData",
    targetProgressValue: {
      immediate: true,
      handler(newValue) {
        this.animateProgress(newValue);
      }
    },
    '$route': 'animateMetrics',
    location: {
      handler() {
        this.fetchDevicesForLocation();
        if (!this.isPipelineLocation) {
        this.animateMetrics();
        }
      },
      deep: true
    }
  },
  computed: {
    isPipelineLocation() {
      // Temporary: Show for any location for testing
      // Change this back to: return this.location && this.location.name === 'West Texas S&B Pipeline';
      console.log('Current location:', this.location);
      return this.location && this.location.name === 'West Texas S&B Pipeline';
      // For testing, uncomment the line below and comment the line above:
      // return true;
    },
    isDemo() {
      return this.$store.state.auth.email.includes("demo@demo");
    },
    fullAddress() {
      return `${this.location.address1}, ${this.location.city}, ${this.location.state} ${this.location.zip}, USA`;
    },
    itemId() {
      return this.$route.params.id;
    },
    location() {
      return this.$store.state.location;
    },
    offlineSensors() {
      if (!this.sensors) return 0;
      return this.sensors.filter(sensor => sensor.status === 'Inactive').length;
    },
    totalSensors() {
      return this.sensors ? this.sensors.length : 0;
    },
    onlineSensors() {
      if (!this.sensors) return 0;
      return this.sensors.filter(sensor => sensor.status === 'Active').length;
    },
    targetProgressValue() {
      return (this.onlineSensors / this.totalSensors) * 100;
    },
    // Generate sensors based on the location's devices array
    sensors() {
      // Default to empty array if location or devices are missing
      const locationDevices = this.location && this.location.devices ? this.location.devices : [];
      
      // Use placeholder data structure but with actual device IDs
      return locationDevices.map((deviceId, index) => {
        // Create variance in the placeholder data based on the index
        const alertCount = index % 3 === 0 ? Math.floor(Math.random() * 3) + 1 : 0;
        const rotation = (index * 37) % 360; // Create different rotation values
        const battery = Math.max(5, 85 - (index * 7) % 95); // Randomize battery levels
        const temp = 20 + (index * 1.7) % 20; // Temperature between 20-40°C
        
        return {
          id: deviceId,
          name: `Device ${deviceId}`,
          status: "Active", // All devices are now active/online
          lastSeen: new Date(Date.now() - (index * 5 * 60000)).toISOString(), // Varying last seen times
          rotationAngle: rotation,
          alerts: alertCount,
          batteryLevel: battery,
          temperature: temp
        };
      });
    },
    // OEE Metrics
    actualParts() {
      return Math.round(this.animatedValues.parts);
    },
    targetParts() {
      return 5240;
    },
    actualCycle() {
      return this.animatedValues.cycle;
    },
    targetCycle() {
      return 70;
    },
    actualUptime() {
      return this.animatedValues.uptime;
    },
    targetUptime() {
      return 85;
    },
    
    // Efficiency Calculations
    partsEfficiency() {
      return this.targetParts ? (this.actualParts / this.targetParts) * 100 : 0;
    },
    cycleEfficiency() {
      return this.actualCycle ? (this.targetCycle / this.actualCycle) * 100 : 0;
    },
    uptimeEfficiency() {
      return this.targetUptime ? (this.actualUptime / this.targetUptime) * 100 : 0;
    },
    
    overallOEE() {
      const efficiencies = [this.partsEfficiency, this.cycleEfficiency, this.uptimeEfficiency];
      const validEfficiencies = efficiencies.filter(e => !isNaN(e) && isFinite(e));
      return validEfficiencies.length ? validEfficiencies.reduce((a, b) => a + b, 0) / validEfficiencies.length : 0;
    },
    
    oeeStatus() {
      const oee = this.overallOEE;
      if (oee >= 90) {
        return { color: 'gren', text: 'ACTIVE' };
      } else if (oee >= 75) {
        return { color: 'yellow', text: 'WARNING' };
      } else {
        return { color: 'red', text: 'CRITICAL' };
      }
    },
    deviceStatusCounts() {
      // Count devices by status
      const counts = {
        Secure: 0,
        Warning: 0,
        Critical: 0
      };
      
      if (!this.sensors || this.sensors.length === 0) return counts;
      
      // Calculate target number of critical devices (about 8% of total)
      const totalDevices = this.sensors.length;
      const targetCritical = Math.ceil(totalDevices * 0.08); // About 8% critical
      const targetWarning = Math.ceil(totalDevices * 0.25); // About 25% warning
      
      // Create a randomized list of indices to ensure non-sequential statuses
      let indices = [...Array(totalDevices).keys()];
      // Fisher-Yates shuffle algorithm
      for (let i = indices.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [indices[i], indices[j]] = [indices[j], indices[i]];
      }
      
      // Create sets for each status
      const criticalIndices = new Set(indices.slice(0, targetCritical));
      const warningIndices = new Set(indices.slice(targetCritical, targetCritical + targetWarning));
      
      // Process each sensor and assign status
      this.sensors.forEach((sensor, index) => {
        if (criticalIndices.has(index)) {
          // Make this a critical device
          counts.Critical++;
          
          // Pick one specific critical device to be offline
          // Using a deterministic but not obvious choice (prime number hash)
          const isOffline = (sensor.id.toString().charCodeAt(sensor.id.length - 1) % 11 === 3) && counts.Critical === targetCritical;
          sensor.status = isOffline ? 'Inactive' : 'Active';
          
          // Set critical rotation angle - varying but high
          sensor.rotationAngle = 45 + Math.floor(Math.random() * 30); // 45-75 degrees
          sensor.alerts = 0; // Critical devices don't need alerts
        }
        // Make some devices show warnings
        else if (warningIndices.has(index)) {
          sensor.status = 'Active';
          sensor.alerts = Math.floor(Math.random() * 3) + 1; // 1-3 alerts
          sensor.rotationAngle = 20 + Math.floor(Math.random() * 20); // 20-40 degrees
          counts.Warning++;
        }
        // The rest are secure
        else {
          sensor.status = 'Active';
          sensor.alerts = 0;
          sensor.rotationAngle = Math.floor(Math.random() * 15); // 0-15 degrees
          counts.Secure++;
        }
      });
      
      return counts;
    },
    deviceStatus() {
      return (sensor) => {
        // Define what makes a sensor Critical, Warning, or Secure based on rotation angle
        if (sensor.rotationAngle >= 45) return "Critical"; // High angle = Critical regardless of connectivity
        if (sensor.alerts > 0) return "Warning";
        return "Secure";
      };
    }
  },
  methods: {
    fetchData() {
      // Your logic to fetch data based on the new route parameter
      this.id = this.$route.params.id;
      this.$store.dispatch("fetchLocationById", this.id);
      // Add your data fetching logic here
      console.log(`Route changed, new id: ${this.id}`);
      // Example: this.loadData(this.id);
    },
    async fetchDevicesForLocation() {
      try {
        // If the location has devices, make sure they're available in the store
        if (this.location && this.location.devices && this.location.devices.length > 0) {
          console.log(`Loading ${this.location.devices.length} devices for location`);
          // You could fetch device details here if needed
          // const devices = await fetchObjects("devices");
          // this.$store.commit("setDevices", devices);
        } else {
          console.log("No devices found for this location");
        }
      } catch (error) {
        console.error("Error fetching devices for location:", error);
      }
    },
   async goToSensor(item) {
      const device = await fetchSingleObjectByKey("devices", item.id);
      this.$store.commit("setSensor", device);
      this.$router.push(
        "/location/" + this.$store.state.location.key + "/device/" + item.id
      );
    },
    animateProgress(target) {
      const duration = 1000; // Animation duration in milliseconds
      const start = this.progressValue;
      const startTime = performance.now();

      const animate = (currentTime) => {
        const elapsed = currentTime - startTime;
        const progress = Math.min(elapsed / duration, 1);

        this.progressValue = start + (target - start) * progress;

        if (progress < 1) {
          requestAnimationFrame(animate);
        }
      };

      requestAnimationFrame(animate);
    },
    async animateMetrics() {
      // Reset all animated values
      this.animatedValues = {
        parts: 0,
        cycle: 0,
        uptime: 0,
        offline: 0,
        total: 0,
        online: 0
      };

      // Get actual counts from sensors
      const realTotal = this.totalSensors;
      const realOnline = this.onlineSensors;
      const realOffline = this.offlineSensors;

      // Animate parts
      const animateParts = async () => {
        for (let i = 0; i <= 4917; i += 123) {
          this.animatedValues.parts = Math.min(i, 4917);
          await new Promise(resolve => setTimeout(resolve, 50));
        }
        this.animatedValues.parts = 4917;
      };

      // Animate cycle and uptime
      const animateMetric = async (key, target, duration) => {
        const steps = 30;
        const interval = duration / steps;
        
        for (let i = 0; i <= steps; i++) {
          const progress = i / steps;
          this.animatedValues[key] = Number((target * progress).toFixed(1));
          await new Promise(resolve => setTimeout(resolve, interval));
        }
        this.animatedValues[key] = target;
      };

      // Animate sensor counts
      const animateCounts = async () => {
        const duration = 1000;
        const steps = 20;
        const interval = duration / steps;
        
        for (let i = 0; i <= steps; i++) {
          const progress = i / steps;
          this.animatedValues.offline = Math.round(realOffline * progress);
          this.animatedValues.total = Math.round(realTotal * progress);
          this.animatedValues.online = Math.round(realOnline * progress);
          await new Promise(resolve => setTimeout(resolve, interval));
        }
        
        // Ensure final values are exact
        this.animatedValues.offline = realOffline;
        this.animatedValues.total = realTotal;
        this.animatedValues.online = realOnline;
      };

      // Start all animations
      await Promise.all([
        animateParts(),
        animateCounts(),
        animateMetric('cycle', 72.1, 1500),
        animateMetric('uptime', 62.8, 1500)
      ]);
    },
    formatLastSeen(timestamp) {
      if (!timestamp) return 'Never';
      const date = new Date(timestamp);
      const now = new Date();
      const diffMinutes = Math.floor((now - date) / (1000 * 60));
      
      if (diffMinutes < 1) return 'Just now';
      if (diffMinutes < 60) return `${diffMinutes}m ago`;
      if (diffMinutes < 1440) return `${Math.floor(diffMinutes/60)}h ago`;
      return date.toLocaleDateString();
    },
    updateFilter(status) {
      this.$store.dispatch('updateStatusFilter', status);
    },
    goToNewDevice() {
      this.$router.push(
        "/location/" + this.$store.state.location.key + "/add-device"
      );
    },
  },
  async created() {
    this.id = this.itemId;
    await this.$store
      .dispatch("fetchLocationById", this.id)
      .then((res) => console.log(res));
    await this.fetchDevicesForLocation();
  },
  mounted() {
    this.animateMetrics();
  },
};
</script>
<style scoped>
.headline {
  font-size: 3rem;
  font-weight: bold;
}

.progress-bar {
  transition: all 0.3s ease;
}

/* Add animation for the striped effect */
.v-progress-linear__stripe {
  animation: progress-stripe 1s linear infinite;
}

@keyframes progress-stripe {
  0% {
    transform: translateX(0);
  }
  100% {
    transform: translateX(20px);
  }
}

.metrics-container {
  margin-top: 1rem;
  animation: fadeIn 0.5s ease-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.metric-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
  font-size: 0.9rem;
}

.metric-row.header {
  margin-bottom: 0.75rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid #eee;
}

.column-header {
  font-size: 0.75rem;
  font-weight: 600;
  color: #666;
  width: 60px;
  text-align: right;
}

.metric-label {
  color: #666;
}

.metric-values {
  display: flex;
  gap: 2rem;
}

.actual, .target {
  width: 60px;
  text-align: right;
  transition: color 0.3s ease;
}

.actual {
  font-weight: 500;
}

.target {
  color: #666;
}

.v-progress-linear {
  border-radius: 4px;
  margin-top: 1.5rem;
}

.v-data-table {
  background: transparent !important;
}

.v-data-table >>> .v-data-table__wrapper {
  overflow-x: auto;
}

.v-data-table >>> tbody tr {
  cursor: pointer;
}

.v-data-table >>> tbody tr:hover {
  background-color: rgba(0,0,0,0.03);
}

.green-chip {
  background-color: #4CAF50 !important;
}
</style>
